import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { CatalogueForm, CatalogueFormData } from '../components/catalogues';
import { DataListFieldRenderer } from '../components/data-lists/DataListFieldRenderer';
import { Button } from '../components/design-system';
import { apiService, DataListColumn } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface CatalogueEditScreenProps {
  catalogueId: string;
}

export const CatalogueEditScreen: React.FC<CatalogueEditScreenProps> = ({
  catalogueId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const { currentBusiness } = useAppStore();
  // Set when this catalogue was reached via a system Catalog Data List's
  // scoped mode (see CataloguesListScreen.tsx) — redirects back into that
  // context on save/cancel instead of the generic catalogue-detail screen.
  const listId: string | undefined = routeParams?.listId;
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<Partial<CatalogueFormData> | null>(null);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);
  // Extra fields (item 2) — only present when this catalogue is linked to a
  // system Catalog Data List that has customColumns configured.
  const [customColumns, setCustomColumns] = useState<DataListColumn[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const [customValueErrors, setCustomValueErrors] = useState<Record<string, string>>({});
  const [savingCustomValues, setSavingCustomValues] = useState(false);

  useEffect(() => {
    if (!listId) return;
    apiService.getDataList(listId).then((res) => {
      if (res.statusCode === 1000 && res.data?.customColumns?.length) {
        setCustomColumns(res.data.customColumns);
      }
    }).catch((err) => console.error('[CatalogueEditScreen] Failed to load linked list:', err));
    apiService.getDataListRecord(listId, catalogueId).then((res) => {
      if (res.statusCode === 1000 && res.data) {
        setCustomValues(res.data.values || {});
      }
    }).catch((err) => console.error('[CatalogueEditScreen] Failed to load custom field values:', err));
  }, [listId, catalogueId]);

  const handleSaveCustomValues = async () => {
    if (!listId) return;
    try {
      setSavingCustomValues(true);
      const res = await apiService.upsertDataListRecordCustomValues(listId, catalogueId, customValues);
      if (res.statusCode === 1000) {
        showNotification('success', 'Success', 'Extra fields updated successfully.');
        setCustomValueErrors({});
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to update extra fields');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to update extra fields');
    } finally {
      setSavingCustomValues(false);
    }
  };

  useEffect(() => {
    loadCatalogue();
  }, [catalogueId]);

  const loadCatalogue = async () => {
    try {
      setLoadingCatalogue(true);
      const response = await apiService.getCatalogueById(catalogueId);
      if (response.statusCode === 1000 && response.data) {
        const catalogue = response.data;
        setInitialData({
          name: catalogue.name,
          description: catalogue.description,
          currency: catalogue.currency,
          locale: catalogue.locale,
          isActive: catalogue.isActive,
        });
      } else {
        showNotification('error', 'Error', 'Failed to load catalogue');
        setTimeout(() => {
          if (Platform.OS === 'web') {
            window.history.back();
          } else {
            navigateTo('catalogues');
          }
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error loading catalogue:', error);
      showNotification('error', 'Error', error.message || 'Failed to load catalogue');
      setTimeout(() => {
        if (Platform.OS === 'web') {
          window.history.back();
        } else {
          navigateTo('catalogues');
        }
      }, 2000);
    } finally {
      setLoadingCatalogue(false);
    }
  };

  const handleSubmit = async (formData: CatalogueFormData) => {
    try {
      setLoading(true);

      // Extra Fields typed into this form are saved with the main button too —
      // previously only their own "Save Extra Fields" button persisted them, so
      // anything typed and then saved via "Update Catalogue" was silently lost.
      // Saved first so the catalog's webhook fires with the values already stored.
      if (listId && customColumns.length > 0) {
        const customRes = await apiService.upsertDataListRecordCustomValues(listId, catalogueId, customValues);
        if (customRes.statusCode !== 1000) {
          showNotification('error', 'Error', customRes.errors?.[0]?.message || 'Failed to update extra fields');
          return;
        }
        setCustomValueErrors({});
      }

      const response = await apiService.updateCatalogue(catalogueId, {
        name: formData.name,
        description: formData.description,
        currency: formData.currency,
        locale: formData.locale,
        isActive: formData.isActive,
      });

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', 'Success', 'Catalogue updated successfully.');
        setTimeout(() => {
          if (listId) {
            navigateTo('data-list-records', listId);
          } else if (Platform.OS === 'web') {
            window.history.back();
          } else {
            navigateTo('catalogue-detail', catalogueId);
          }
        }, 1500);
      } else {
        const errorMessage =
          response.errors?.[0]?.message || 'Failed to update catalogue';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error updating catalogue:', error);
      showNotification('error', 'Error', error.message || 'Failed to update catalogue');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('catalogue-detail', catalogueId);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loadingCatalogue) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        formHeader={<FormHeader title="Edit Catalogue" onBack={() => navigateTo('catalogues')} />}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading catalogue...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title="Edit Catalogue" onBack={handleCancel} />}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' && styles.contentContainerWeb,
          Platform.OS !== 'web' && styles.contentContainerMobile
        ]}
        showsVerticalScrollIndicator={false}
      >
        {initialData && (
          <CatalogueForm
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Update Catalogue"
            loading={loading}
          />
        )}

        {/* Extra Fields (item 2) — custom columns on the linked system Data
            List, alongside this catalogue's own fixed fields above. */}
        {listId && customColumns.length > 0 && currentBusiness?._id && (
          <View style={styles.customFieldsSection}>
            <Text style={styles.customFieldsSectionTitle}>Extra Fields</Text>
            {customColumns.map((col) => (
              <DataListFieldRenderer
                key={col.id}
                column={col}
                value={customValues[col.id]}
                onChange={(v) => setCustomValues((prev) => ({ ...prev, [col.id]: v }))}
                error={customValueErrors[col.id]}
                businessId={currentBusiness._id}
                listId={listId}
                recordId={catalogueId}
              />
            ))}
            <Button
              title="Save Extra Fields"
              onPress={handleSaveCustomValues}
              loading={savingCustomValues}
              variant="secondary"
            />
          </View>
        )}
      </ScrollView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  customFieldsSection: {
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
    backgroundColor: colors.background.surface as string,
  },
  customFieldsSectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.md,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100,
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});

